/**
 * PreferenceBadge
 *
 * Simple icon + label chip for fixed-value preferences like portion preference.
 * Pill-shaped badge with gradient background.
 */
import { Box, HStack, VStack, Tooltip, Spacer } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { Utensils } from 'lucide-react'
import { FoodTraitBadge } from '../FoodTraitBadge'
import { getPortionIcon, getPortionLabel } from './preferenceUtils'
import { PreferenceCardHeader } from './PreferenceCardHeader'

const MotionBox = motion.create(Box)

interface PreferenceBadgeProps {
  /** The raw portion preference value */
  value: string
  /** Full description for tooltip (optional) */
  description?: string
}

// Map portion icon types to emojis
function getPortionEmoji(iconType: string): string {
  switch (iconType) {
    case 'users':
      return '👥'
    case 'user':
      return '👤'
    default:
      return '🍽️'
  }
}

export function PreferenceBadge({ value, description }: PreferenceBadgeProps) {
  const iconType = getPortionIcon(value)
  const label = getPortionLabel(value)
  const emoji = getPortionEmoji(iconType)

  const badge = (
    <MotionBox
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        bg="var(--card-surface-2)"
        borderRadius="var(--r-card-compact)"
        p={3}
        h="100%"
        cursor={description ? 'pointer' : 'default'}
        _hover={{ bg: 'var(--card-chip-hover)' }}
        transition="background 0.2s"
      >
        <VStack align="stretch" spacing={2} h="100%">
          {/* Row 1: Header */}
          <PreferenceCardHeader icon={Utensils} label="Portion Style" color="pink.400" />

          <Spacer />
          {/* Row 2: Badge pill */}
          <HStack justify="center">
            <FoodTraitBadge
              icon={emoji}
              label={label}
              bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            />
          </HStack>
          <Spacer />
        </VStack>
      </Box>
    </MotionBox>
  )

  if (description) {
    return (
      <Tooltip
        label={description}
        placement="top"
        hasArrow
        bg="gray.800"
        color="white"
        fontSize="xs"
        px={3}
        py={2}
        borderRadius="var(--r-chip)"
        maxW="280px"
      >
        {badge}
      </Tooltip>
    )
  }

  return badge
}
